import { DocumentEvents } from "./../helpers/document-events";
import { transient } from "aurelia-dependency-injection";
import { AppSettings } from "../../app-settings";
import { prop } from "../../decorators/property";
import { BaseDocumentModel } from "../../desktop/models/base-document";
import { ParagraphFilterModel } from "../../desktop/models/paragraph-filter";
import { CaseRelationModel } from "../../desktop/models/case-relation";
import { ReportDetailModel } from "../../desktop/models/report-detail";
import { DisplayKeyParagraphCollection } from "../../desktop/collections/display-key-paragraphs";
import { DisplayKeyParagraphModel } from "../../desktop/models/display-key-paragraph";
import { AuthorizationModel } from "../../authentication/models/authorization-model";
import { backboneModelProperties } from "../../decorators/backbone-properties";
import { AppEvents } from "../../app-events";
import defaults from "../../desktop/models/document-defaults";
import { IDocumentModel } from "../../desktop/interfaces/document-model";
import "../../desktop/interfaces/document-update-response";
import { FullTextDetailModel } from "./full-text-detail";

@transient()
@backboneModelProperties(defaults)
export class CaseDocumentModel extends BaseDocumentModel implements IDocumentModel {

    @prop() public Considered: Backbone.Collection<CaseRelationModel> = new Backbone.Collection<CaseRelationModel>();
    @prop() public Subsequent: Backbone.Collection<CaseRelationModel> = new Backbone.Collection<CaseRelationModel>();
    /** Non-official citation list. List of citations that are automatically generated, but not approved by the editorial team */
    @prop() public NOCList: Backbone.Collection<CaseRelationModel>;
    @prop() public LegislationConsidered: Backbone.Collection<any>;
    @prop() public HiddenTreatmentCount: number;
    @prop() public CitingParagraphFilters: Backbone.Collection<ParagraphFilterModel>;
    @prop() public DisplayPowerParagraphs: Backbone.Collection<DisplayKeyParagraphModel>;
    @prop() public DisplayQuotedParagraphs: Backbone.Collection<DisplayKeyParagraphModel>;
    @prop() public AvailableTreatmentTypes: any;
    @prop() public JudgmentDate: string;
    @prop() public ReportDetails: Backbone.Collection<ReportDetailModel>;
    @prop() public FullTextDetails: Backbone.Collection<FullTextDetailModel>;
    @prop() public HasPdf: boolean;
    @prop() public HasJurisdictionsFilter: boolean;
    @prop() public ReportButtonsReady: boolean;
    @prop() public ReportTabsReady: boolean;
    @prop() public HasTranscripts: boolean;
    @prop() public HasReports: boolean;
    @prop() public JurisdictionsFilterCount: number;
    @prop() public UniqueReportList: ReportDetailModel[];
    @prop() public Digest: string;
    @prop() public CiteLineAvailable: boolean;
    @prop() public IsHighestStage: boolean;
    @prop() public HasSeveralStagesOnActiveStageCourt: boolean;
    @prop() public UpgradeRequired: boolean;
    @prop() public NonLoadedNeutralCitedCasesCount: number;
    @prop() public NonLoadedNeutralCitingCasesCount: number;
    @prop() public FullTextType: string;

    public citationsInContextDictionary: any = {};

    constructor(options?) {
        super(options);
        this.setupEventListeners();
    }

    public cleanOut() {
        const preserve = {
            Url: this.Url,
            Title: this.Title,
            Id: this.Id,
            ParentId: this.ParentId,
            RenderType: this.RenderType
        };
        const recover = _.extend(this.defaults, preserve);
        this.clear().set(recover);
    }

    public refresh(): Promise<IDocumentModel> {
        return new Promise<IDocumentModel>((resolve) => {
            const params = $.param({ type: this.RenderType, id: this.id, skipNeutralAndUnclassifiedTreatments: this.mobileHelper.IsMobileMode && this.mobileMinimalLoading });
            this.fetch({
                data: params,
                headers: {
                    UserSettings : this.getUserSettings()
                }
            }).done((data) => {
                resolve(data);
                this.trigger(DocumentEvents.DocumentRefreshed);
            });
        });
    }

    public getTooltipForJurisdictionShowAllButton(showOnlyMyJurisdiction: boolean): string {
        return showOnlyMyJurisdiction ? "Show only my jurisdictions" : "Show all jurisdictions";
    }

    public addCitingParagraphFilter(sourceId: string, color: string) {
        let filters = this.CitingParagraphFilters;
        if (!filters) {
            filters = new Backbone.Collection<ParagraphFilterModel>();
        }
        filters.add([{ id: sourceId, color }]);
        this.CitingParagraphFilters = filters;
    }

    public removeCitingParagraphFilter(sourceId: string) {
        let filters = this.CitingParagraphFilters;
        if (!filters) {
            filters = new Backbone.Collection<ParagraphFilterModel>();
        }
        (filters as any).remove(sourceId);
        this.CitingParagraphFilters = filters;
    }

    public loadFullText(): Promise<{}> {
        return new Promise<any>((resolve) => {
            if (this.FullTextLoaded) {
                 resolve();
            } else {
                this.getFulltextFromServer(this.RenderType, this.Id).then((response) => {
                    this.FullText = response.Fulltext;
                    if (this.FullText && this.FullText.length > 0) {
                        this.HasTranscripts = true;
                    } else {
                        this.HasFullText = false;
                    }
                    this.PdfLinks = response.PdfDetails;
                    this.HasPdf = (response.PdfDetails !== null && response.PdfDetails.length > 0 && this.checkDocHasOkPdfLink(response.PdfDetails));
                    if (response.ReportDetails && response.ReportDetails.length) {
                        this.ReportDetails = response.ReportDetails;
                        this.HasReports = true;

                        const mappedDetails = this.ReportDetails.map((details) => {
                            return {ReportName: details.ReportName, Source: details.Source};
                        });

                        this.UniqueReportList = _.uniq(mappedDetails, false, (details) => {
                            return details.ReportName;
                        });
                    }
                    this.FullTextLoaded = true;
                    this.FullTextType = response.FulltextType === 0 ? "HTML" : "XML";
                    this.FullTextDetails = response.FullTextDetails;
                    resolve();
                });
            }
        });
    }

    public loadQuotedParagraphs(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            if (this.has("DisplayQuotedParagraphs")) {
                resolve();
            } else {
                const collection = new DisplayKeyParagraphCollection();
                collection.fetchRequest({
                    url: AppSettings.ApiUrlPrefix + "keyparagraphs/quoted/" + this.id,
                    beforeSend: AuthorizationModel.attachAuthorizationHeader,
                    success: () => {
                        this.DisplayQuotedParagraphs = collection;
                        resolve();
                    },
                    error: (err) => {
                        dev.log(`Error loading quoted key paragraphs for document ${this.id}`);
                        dev.log("%s: %s", err.status, err.responseText);
                        reject();
                    }
                });
            }
        });
    }

    public runPriorityProcess(data: any) {
        const options: any = {
            DocKeys: [data.Id],
            QueueName: "Priority"
        };
        const controllerName = this.getControllerNameFromProcess(data.processName);
        const url = AppSettings.ProcessingApiUrlPrefix + controllerName;

        if (controllerName.toLowerCase() === "patches") {
            options.Patch = data.processName;
        }
        return Promise.resolve($.ajax({
                url,
                type: "POST",
                data: options,
                success: (successData) => {
                    this.appEvents.trigger(AppEvents.InformationMessage, successData);
                },
                beforeSend: AuthorizationModel.attachAuthorizationHeader
         }));

    }

    private setupEventListeners(): void {
        this.appEvents.on(AppEvents.JurisdictionsSaved, () => {
            this.FullTextLoaded = false;
            this.refresh();
        });
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/models/case-document.ts